<template>
    <div>
        <h1>All Packages</h1>

        <div v-if="isTableLoading" class="text-center pt-3">
            <b-spinner label="Loading..." />
        </div>

        <template v-else>

            <div class="custom-search d-flex justify-content-end">
                <b-form-group>
                    <b-button variant="primary" class="float-right" v-b-modal.edit-modal @click="openAddModal">
                        <span> Add package </span>
                    </b-button>
                </b-form-group>
            </div>

            <vue-good-table
                    :columns="columns"
                    :rows="packages"
                    :search-options="{
                      enabled: true
                    }"
                    :pagination-options="{
                      enabled: true,
                      perPage: pageLength,
                    }"
                >
                <template #table-row="props">
                    <!-- Column: Action -->
                    <span v-if="props.column.field === 'action'" class="action-link-group">
                            <b-dropdown
                              variant="link"
                              toggle-class="text-decoration-none"
                              no-caret
                              right
                            >
                                <template v-slot:button-content>
                                  <feather-icon
                                          icon="MoreVerticalIcon"
                                          size="16"
                                          class="text-body align-middle"
                                  />
                                </template>
                                <b-dropdown-item
                                        v-if="props.row.payment_status !== 6"
                                        v-b-modal.edit-modal
                                        @click="openEditModal(props.row)"
                                    >
                                    <feather-icon icon="Edit2Icon" class="mr-50" />
                                    <span>Edit</span>
                                </b-dropdown-item>

                                <b-dropdown-item @click="deleteItem(props.row.id)">
                                    <feather-icon icon="TrashIcon" class="mr-50" />
                                    <span>Delete</span>
                                </b-dropdown-item>
                            </b-dropdown>
                    </span>
                    <span v-else-if="props.column.field === 'inactive'">
                      <b-badge :variant="status[props.row.inactive].class">
                        {{ props.row.inactive == 0 ? 'Active' : 'Inactive' }}
                      </b-badge>
                    </span>
                    <DatatableCellValue
                      v-else
                      :field="props.column.field"
                      :value="props.formattedRow[props.column.field]"
                    />
                </template>

                <!-- pagination -->
                <template #pagination-bottom="props">
                    <TablePagination
              :value="1"
              :total-rows="props.total"
              :per-page="pageLength"
              @input="(value) => props.pageChanged({ currentPage: value })"
              @per-page-change="(n) => { pageLength = n; props.perPageChanged({ currentPerPage: n }) }"
            />
                </template>
            </vue-good-table>
        </template>

        <b-modal id="edit-modal" :title="edited.unit_id > 0 ? 'Edit package details' : 'Add new package'">

            <b-form>

              <b-form-group label="Unit:" label-for="input-name">
                <v-select required
                  id="unit_id"
                  v-model="edited.unitObject"
                  item-value="id"
                  label="name"
                  item-text="name"
                  :options="units"
                  :clearable="false"
                />
                <small class="text-danger" v-if="v$.edited.unitObject.$error">Unit is required.</small>
              </b-form-group>

                <b-form-group label="Name:" label-for="input-name">
                    <b-form-input id="name" v-model="edited.name" placeholder="Package Name" required />
                    <small class="text-danger" v-if="v$.edited.name.$error">Name is required.</small>
                </b-form-group>

                <b-form-group label="Price:" label-for="input-name">
                    <b-form-input id="price" type="number" v-model="edited.price" placeholder="Package Price" required />
                    <small class="text-danger" v-if="v$.edited.price.$error">Price is required.</small>
                </b-form-group>

                <b-form-group label="Price for additional people:" label-for="input-name">
                  <b-form-input id="additional_price" type="number" v-model="edited.additional_price" placeholder="Additional Price"/>
                  <small class="text-danger">{{ /*errors[0]*/ '' }}</small>
                </b-form-group>

                <b-form-group label="Deposit (%):" label-for="input-name">
                    <b-form-input id="deposit" type="number" v-model="edited.deposit" placeholder="Deposit"/>
                    <small class="text-danger">{{ /*errors[0]*/ '' }}</small>
                </b-form-group>

              <b-form-group label="Start time (HH:MM):" label-for="start_time">
                <b-form-input id="start_time" type="text" v-model="edited.start_time" placeholder="Start time. Ex: 15:25"/>
                <small class="text-danger" v-if="v$.edited.start_time.$error">Start time is required.</small>
              </b-form-group>

              <b-form-group label="Duration (in hours):" label-for="duration">
                <b-form-input id="duration" type="number" v-model="edited.duration" placeholder="Duration in hours"/>
                <small class="text-danger" v-if="v$.edited.duration.$error">Duration is required.</small>
              </b-form-group>

                <b-form-group label="Active from:" label-for="input-name">
                    <b-form-datepicker
                        id="active_from"
                        :min="minDate"
                        v-model="edited.active_from"
                        locale="en"
                    />
                    <small class="text-danger">{{ /*errors[0]*/ '' }}</small>
                </b-form-group>

                <b-form-group label="Active to:" label-for="input-name">
                  <b-form-datepicker
                      id="active_to"
                      :min="minDate"
                      v-model="edited.active_to"
                      locale="en"
                      @context="onContext"
                  />
                  <small class="text-danger">{{ /*errors[0]*/ '' }}</small>
                </b-form-group>

                <b-form-checkbox v-model="edited.inactive" switch class="mb-1">
                    Inactive Package
                </b-form-checkbox>
            </b-form>

            <template #modal-footer>
                <b-button variant="outline-secondary" class="float-right" @click="$bvModal.hide('edit-modal')">
                    Close
                </b-button>
                <b-button variant="primary" class="float-right" @click="editSubmit" :disabled="isEditLoading">
                    <b-spinner v-if="isEditLoading" label="Spinning" small />
                    <span v-else> Save </span>
                </b-button>
            </template>

        </b-modal>

    </div>
</template>

<script>
    import useVuelidate from '@vuelidate/core';
    import { required, numeric, integer, minValue, minLength, email, helpers } from '@vuelidate/validators';

    import {
        BBadge,
        BPagination,
        BFormGroup,
        BFormInput,
        BDropdown,
        BDropdownItem,
        VBModal,
        BForm,
        BFormDatepicker,
        BSpinner,
        BButton,
        BFormCheckbox,
        BTable,
        VBTooltip,
    } from "bootstrap-vue";
    import 'vue-good-table-next/dist/vue-good-table-next.css'
    import vSelect from "vue-select";
    import { VueGoodTable } from "vue-good-table-next";
    import { mapActions, mapGetters } from "vuex"
import TablePagination from '@/views/Components/Elements/TablePagination.vue';
    import axios from "@/libs/axios";
    import { toastSuccess, toastFailure } from "@/libs/toastification";
    import { sweetWarning, sweetError } from "@/libs/sweet-alerts";
    import DatatableCellValue from '@/views/Components/Elements/DatatableCellValue.vue'

    export default {
        components: {
    TablePagination,
            DatatableCellValue,
            VueGoodTable,
            BBadge,
            BPagination,
            BFormGroup,
            BFormInput,
            BDropdown,
            BDropdownItem,
            BForm,
            vSelect,
            BFormDatepicker,
            BSpinner,
            BButton,
            BFormCheckbox,
            BTable,
        },
        directives: {
            "b-modal": VBModal,
            "b-tooltip": VBTooltip,
        },
        data() {
            return {
                status: {
                  0: {
                    label: "Inactive",
                    class: "light-primary",
                  },
                  1: {
                    label: "Active",
                    class: "light-dark",
                  }
                },
                minDate: new Date(),
                isTableLoading: true,
                isEditLoading: false,
                isAddingLoading: false,
                columns: [

                    {
                        label: "Name",
                        field: "name",
                        type: "string",
                        filterOptions: {
                            enabled: true,
                            placeholder: "All",
                            trigger: "enter",
                        },
                    },
                    {
                        label: "Unit",
                        field: "unit_name",
                        type: "string",
                        filterOptions: {
                            enabled: true,
                            placeholder: "All",
                            trigger: "enter",
                        },
                    },
                    {
                        label: "Price",
                        field: "price",
                        width: '140px',
                        type: 'float',
                        filterOptions: {
                            enabled: true,
                            placeholder: "All",
                            trigger: "enter",
                        },
                    },
                    {
                        label: "Additional Price",
                        field: "additional_price",
                        width: '200px',
                        type: 'float',
                        filterOptions: {
                            enabled: true,
                            placeholder: "All",
                            trigger: "enter",
                        },
                    },
                    {
                        label: "Deposit (%)",
                        field: "deposit",
                        width: '140px',
                        type: 'float',
                        filterOptions: {
                            enabled: true,
                            placeholder: "All",
                            trigger: "enter",
                        },
                    },
                    {
                        label: "Status",
                        field: "inactive",
                        sortable: true,
                    },
                    {
                        label: "Action",
                        field: "action",
                        thClass: "text-center",
                        tdClass: "text-center",
                        sortable: false,
                    }
                ],
                pageLength: 10,
                edited: {
                    id: null,
                    unit_id: null,
                    name: null,
                    price: null,
                    deposit: null,
                    additional_price: null,
                    active_from: null,
                    active_to: null,
                    inactive: null,
                    unitObject: null
                }
            };
        },
        computed: {
            ...mapGetters({
                packages: "packages/packages",
                units: 'units/units',
            }),
        },
        watch: {
            ////
        },
        async mounted() {

            await this.loadPackages()
                .then(()=>{
                    this.loadUnits()
                        .catch(() => { toastFailure('Something went wrong.') });
                })
                .catch(() => {
                    toastFailure('Something went wrong.')
                });
            this.isTableLoading = false;
        },
        validations() {

          const validTimeFormat = helpers.regex(/^([01][0-9]|2[0-3]):([0-5][0-9])$/);

          return {
              edited: {
                name: { required, minLength: 3 },
                start_time: { required, validTimeFormat },
                unitObject: { required },
                price: { required, numeric },
                duration: { required, numeric, minValue: minValue(0.1)}
              }
          };
        },
        setup() {
          return { v$: useVuelidate() };
        },
        methods: {
            ...mapActions({
                loadPackages: "packages/loadPackages",
                updatePackage: "packages/updatePackage",
                deletePackage: "packages/deletePackage",
                addPackage: "packages/addPackage",
                loadUnits: 'units/loadUnits',
            }),
            onContext(ctx) {
                this.formatted = ctx.selectedFormatted;
                this.selected = ctx.selectedYMD;
            },
            showError(responseData=null, warning=false) {

                if( responseData != null && responseData.message != undefined && responseData.message != null ){

                    let errorMSG = responseData.message + '</br>';
                    if(responseData.errors){

                        Object.keys(responseData.errors).map(function(errorKey){
                            responseData.errors[errorKey].map(function(value, key){
                                errorMSG += value + '</br>';
                            });
                        });
                    }

                    if( warning ){
                        sweetWarning('Warning', errorMSG);
                    }
                    else{
                        sweetError('Error', errorMSG);
                    }
                }
                else{
                    toastFailure("Something went wrong.");
                }
            },
            openEditModal(row) {

                this.edited.id = row.id;
                this.edited.unit_id = row.unit_id;
                this.edited.unit_name = row.unit_name;
                this.edited.name = row.name;
                this.edited.price = row.price;
                this.edited.deposit = row.deposit;
                this.edited.additional_price = row.additional_price;
                this.edited.active_from = row.active_from;
                this.edited.active_to = row.active_to;
                this.edited.inactive = row.inactive == 1 ? true : false;
                this.edited.unitObject = this.units.find(x => x.id === row.unit_id);
                this.edited.start_time = row.start_time;
                this.edited.duration = row.duration;
            },
            openAddModal() {

                this.edited.id = 0;
                this.edited.unit_id = 0;
                this.edited.unit_name = '';
                this.edited.name = '';
                this.edited.price = '';
                this.edited.deposit = 0;
                this.edited.additional_price = 0;
                this.edited.active_from = '';
                this.edited.active_to = '';
                this.edited.inactive = false;
                this.edited.unitObject = null;
                this.edited.start_time = '';
                this.edited.duration = '';
            },

            async deleteItem(id) {
                if (await this.confirmationMessage('Are you sure you want to delete this package?')) {
                    this.deletePackage(id)
                        .then(() => {
                            toastSuccess("Package has been deleted.");
                        })
                        .catch(() => {
                            toastFailure("Something went wrong while deleting the package");
                        });
                }
            },

            async editSubmit() {

              const isFormCorrect = await this.v$.$validate()
              if (!isFormCorrect) return

                this.isEditLoading = true;
                if (this.edited.id > 0) {

                  this.updatePackage({
                    id: this.edited.id,
                    name: this.edited.name,
                    price: this.edited.price,
                    deposit: this.edited.deposit,
                    additional_price: this.edited.additional_price,
                    unit_id: this.edited.unitObject.id,
                    unit_name: this.edited.unit_name,
                    active_from: this.edited.active_from,
                    active_to: this.edited.active_to,
                    inactive: this.edited.inactive,
                    start_time: this.edited.start_time,
                    duration: this.edited.duration,
                  })
                      .then(() => {
                        toastSuccess("Your changes have been updated.");
                      })
                      .catch((error) => {
                        toastFailure("Something went wrong while updating the package.");
                      })
                      .finally(() => {
                        this.isEditLoading = false;
                        this.$bvModal.hide('edit-modal');
                      });
                } else {
                  this.addPackage({
                    name: this.edited.name,
                    price: this.edited.price,
                    deposit: this.edited.deposit,
                    additional_price: this.edited.additional_price,
                    unit_id: this.edited.unitObject.id,
                    unit_name: this.edited.unit_name,
                    active_from: this.edited.active_from,
                    active_to: this.edited.active_to,
                    inactive: this.edited.inactive,
                    start_time: this.edited.start_time,
                    duration: this.edited.duration,
                  })
                      .then(() => {
                        toastSuccess("New package added.");
                      })
                      .catch((error) => {
                        toastFailure("Something went wrong while adding a new package.");
                      })
                      .finally(() => {
                        this.isEditLoading = false;
                        this.$bvModal.hide('edit-modal');
                      });
                }
            },

            onColumnFilter(params) {
                this.changeUrl(params.columnFilters);
            },
            changeUrl(values) {
                const searchParams = new URLSearchParams(window.location.search);

                /* eslint-disable-next-line */
                for (const [key, value] of Object.entries(values)) {
                    searchParams.set(key, value);
                }

                window.history.pushState(
                    {},
                    null,
                    `${this.$route.path}?${searchParams.toString()}`
                );
            },

            confirmationMessage(message) {
                return this.$bvModal.msgBoxConfirm(message, {
                    title: 'Please Confirm',
                    okVariant: 'primary',
                    okTitle: 'Yes',
                    cancelTitle: 'No',
                });
            }
        },
    };
</script>

<style>
</style>
